import React, { useEffect, useState } from 'react';
import { XIcon, CameraIcon, LockIcon, KeyIcon, ChevronDownIcon } from 'lucide-react';
import { useUser, UserRole } from '../contexts/UserContext';
interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function EditProfileModal({
  isOpen,
  onClose
}: EditProfileModalProps) {
  const {
    role: globalRole,
    setRole: setGlobalRole,
    name: globalName,
    setName: setGlobalName,
    email: globalEmail,
    setEmail: setGlobalEmail
  } = useUser();
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [name, setName] = useState(globalName);
  const [email, setEmail] = useState(globalEmail);
  const [role, setRole] = useState<UserRole>(globalRole);
  const [country, setCountry] = useState('México');
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowPasswordSection(false);
      setName(globalName);
      setEmail(globalEmail);
      setRole(globalRole);
      setCountry('México');
    }
  }, [isOpen, globalName, globalEmail, globalRole]);
  if (!isOpen) return null;
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  const countries = ['México', 'Estados Unidos', 'España', 'Colombia', 'Argentina', 'Brasil', 'Chile', 'Perú', 'Alemania', 'Francia'];
  const roles = [{
    value: 'authenticated-public-user',
    label: 'Authenticated Public User'
  }, {
    value: 'customer',
    label: 'Customer'
  }, {
    value: 'dealer',
    label: 'Dealer'
  }, {
    value: 'sales-manager',
    label: 'Sales Manager'
  }, {
    value: 'operations-manager',
    label: 'Operations Manager'
  }, {
    value: 'manager-business-unit',
    label: 'Manager Business Unit'
  }, {
    value: 'ceo',
    label: 'CEO'
  }, {
    value: 'logistic',
    label: 'Logistic'
  }, {
    value: 'marketing',
    label: 'Marketing'
  }, {
    value: 'financial-manager',
    label: 'Financial Manager'
  }, {
    value: 'system-admin',
    label: 'System Admin'
  }, {
    value: 'dealer-admin',
    label: 'Dealer Admin'
  }, {
    value: 'region-admin',
    label: 'Region Admin'
  }];
  return <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={handleBackdropClick} data-id="element-3024">
      <div className="bg-[#0f1d3a] rounded-2xl shadow-2xl border border-white/10 w-full max-w-lg my-8 relative flex flex-col max-h-[90vh]" data-id="element-3025">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0" data-id="element-3026">
          <h2 className="text-white font-semibold text-lg" data-id="element-3027">Edit Profile</h2>
          <button onClick={onClose} className="text-blue-300 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10" data-id="element-3028">
            <XIcon size={20} data-id="element-3029" />
          </button>
        </div>

        {/* Content (Scrollable) */}
        <div className="p-6 overflow-y-auto flex-1 no-scrollbar" data-id="element-3030">
          {/* Avatar Section */}
          <div className="flex flex-col items-center mb-8" data-id="element-3031">
            <div className="relative" data-id="element-3032">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center ring-4 ring-[#0f1d3a] shadow-lg" data-id="element-3033">
                <span className="text-2xl font-bold text-white" data-id="element-3034">DA</span>
              </div>
              <button className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-500 text-white rounded-full p-1.5 shadow-lg border-2 border-[#0f1d3a] transition-colors" title="Change photo" data-id="element-3035">
                <CameraIcon size={14} data-id="element-3036" />
              </button>
            </div>
            <span className="text-blue-400 text-xs mt-2 font-medium cursor-pointer hover:text-blue-300 transition-colors" data-id="element-3037">
              Change photo
            </span>
            <span className="inline-flex items-center mt-3 px-3 py-1 rounded-full text-xs font-semibold bg-teal-500/20 text-teal-300 border border-teal-500/30" data-id="element-3038">
              Dealer
            </span>
          </div>

          {/* Form Fields */}
          <div className="space-y-5" data-id="element-3039">
            <div data-id="element-3040">
              <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3041">
                Full name
              </label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Your name" data-id="element-3042" />
            </div>

            <div data-id="element-3043">
              <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3044">
                Email
              </label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="your@email.com" data-id="element-3045" />
            </div>

            <div data-id="element-3046">
              <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3047">
                Role
              </label>
              <div className="relative" data-id="element-3048">
                <select value={role} onChange={e => setRole(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-3049">
                  {roles.map(r => <option key={r.value} value={r.value} className="bg-slate-800 text-white" data-id="element-3050">
                      {r.label}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3051">
                  <ChevronDownIcon size={16} data-id="element-3052" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-3053">
              <div data-id="element-3054">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3055">
                  Country
                </label>
                <div className="relative" data-id="element-3056">
                  <select value={country} onChange={e => setCountry(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-3057">
                    {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-3058">
                        {c}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3059">
                    <ChevronDownIcon size={16} data-id="element-3060" />
                  </div>
                </div>
              </div>

              <div data-id="element-3061">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3062">
                  Region
                </label>
                <div className="relative" data-id="element-3063">
                  <input type="text" value="Aisco America" readOnly className="w-full bg-white/5 border border-white/5 rounded-lg pl-10 pr-4 py-2.5 text-blue-300/70 cursor-not-allowed focus:outline-none" data-id="element-3064" />
                  <LockIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400/50" data-id="element-3065" />
                </div>
              </div>
            </div>

            {/* Change Password Section */}
            <div className="pt-4 border-t border-white/10 mt-6" data-id="element-3066">
              <button type="button" onClick={() => setShowPasswordSection(!showPasswordSection)} className="flex items-center justify-between w-full text-left group" data-id="element-3067">
                <div className="flex items-center text-blue-400 group-hover:text-blue-300 transition-colors" data-id="element-3068">
                  <KeyIcon size={18} className="mr-2" data-id="element-3069" />
                  <span className="font-medium text-sm" data-id="element-3070">Change password</span>
                </div>
                <ChevronDownIcon size={18} className={`text-blue-400 group-hover:text-blue-300 transition-transform duration-200 ${showPasswordSection ? 'rotate-180' : ''}`} data-id="element-3071" />
              </button>

              {showPasswordSection && <div className="mt-4 space-y-4 animate-in slide-in-from-top-2 fade-in duration-200" data-id="element-3072">
                  <div data-id="element-3073">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3074">
                      New password
                    </label>
                    <input type="password" className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="••••••••" data-id="element-3075" />
                  </div>
                  <div data-id="element-3076">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3077">
                      Confirm password
                    </label>
                    <input type="password" className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="••••••••" data-id="element-3078" />
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end space-x-3 flex-shrink-0 bg-black/20 rounded-b-2xl" data-id="element-3079">
          <button onClick={onClose} className="bg-white/10 hover:bg-white/15 text-blue-200 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors" data-id="element-3080">
            Cancel
          </button>
          <button onClick={() => {
          setGlobalRole(role);
          setGlobalName(name);
          setGlobalEmail(email);
          onClose();
        }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-3081">
            Save changes
          </button>
        </div>
      </div>
    </div>;
}